/**
 * CommunitySidebar - Left sidebar navigation for community tools.
 *
 * Displayed when viewing a specific community (routes under /communities/:id/...).
 * Provides navigation to: Dashboard, Forecasting, Modelling & Optimization,
 * Data Management, and community setup pages.
 */

import React from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import '../styles/community-layout.css'

function CommunitySidebar({ community, collapsed, onToggle }) {
  const location = useLocation()
  const { id } = useParams()
  const { t } = useTranslation()
  const communityId = id || community?.id

  const status = community?.metadata?.status || 'setup'

  const isActive = (path) => {
    if (path.endsWith('/dashboard')) {
      return location.pathname === path
    }
    return location.pathname.startsWith(path)
  }

  const navItems = [
    {
      section: t('community_sidebar.section_overview'),
      items: [
        {
          label: t('community_sidebar.nav_dashboard'),
          path: `/communities/${communityId}/dashboard`,
          icon: '⌂',
          iconText: 'DAS',
        },
      ],
    },
    {
      section: t('community_sidebar.section_energy_tools'),
      items: [
        {
          label: t('community_sidebar.nav_forecasting'),
          path: `/communities/${communityId}/forecasting`,
          icon: '⇆',
          iconText: 'FOR',
          badge: null,
        },
        {
          label: t('community_sidebar.nav_optimization'),
          path: `/communities/${communityId}/modelling`,
          icon: '⚡',
          iconText: 'OPT',
          badge: null,
        },
        {
          label: t('community_sidebar.nav_past_results'),
          path: `/communities/${communityId}/results`,
          icon: '\u{1F4CB}',
          iconText: 'RES',
          badge: null,
        },
      ],
    },
    {
      section: t('community_sidebar.section_management'),
      items: [
        {
          label: t('community_sidebar.nav_assets'),
          path: `/communities/${communityId}/assets`,
          icon: '⚙',
          iconText: 'AST',
        },
        {
          label: t('community_sidebar.nav_buildings'),
          path: `/communities/${communityId}/buildings`,
          icon: '\u{1F3E2}',
          iconText: 'BLD',
        },
        {
          label: t('community_sidebar.nav_data'),
          path: `/communities/${communityId}/data`,
          icon: '▶',
          iconText: 'DAT',
        },
      ],
    },
  ]

  return (
    <>
      <nav className={`community-sidebar ${collapsed ? 'collapsed' : ''}`}>
        {/* Sidebar Header - Community Info */}
        <div className="sidebar-header">
          <div className="sidebar-community-name" title={community?.name || t('community_sidebar.loading')}>
            {community?.name || t('community_sidebar.loading')}
          </div>
          <div className="sidebar-community-meta">
            <span className={`sidebar-status-dot ${status}`} />
            <span>{status === 'active' ? t('community_sidebar.status_active') : t('community_sidebar.status_setup')}</span>
            {community?.metadata?.country && (
              <>
                <span style={{ opacity: 0.3 }}>|</span>
                <span>{community.metadata.country}</span>
              </>
            )}
          </div>
        </div>

        {/* Navigation Sections */}
        <ul className="sidebar-nav">
          {navItems.map((section) => (
            <React.Fragment key={section.section}>
              <li className="sidebar-section-label">{section.section}</li>
              {section.items.map((item) => {
                const active = item.exact
                  ? location.pathname === item.path
                  : isActive(item.path)

                return (
                  <li key={item.path} className="sidebar-nav-item">
                    <Link
                      to={item.path}
                      className={`sidebar-nav-link ${active ? 'active' : ''}`}
                    >
                      <span className="sidebar-nav-icon">{item.icon}</span>
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className="sidebar-nav-badge">{item.badge}</span>
                      )}
                    </Link>
                  </li>
                )
              })}
            </React.Fragment>
          ))}
        </ul>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <Link to="/communities">
            {t('community_sidebar.back_all_communities')}
          </Link>
        </div>
      </nav>

      <button
        className="sidebar-toggle"
        onClick={onToggle}
        title={collapsed ? t('community_sidebar.expand') : t('community_sidebar.collapse')}
        aria-label={collapsed ? t('community_sidebar.expand') : t('community_sidebar.collapse')}
        style={collapsed ? { left: '1rem' } : { left: '270px' }}
      >
        {collapsed ? '▶' : '◀'}
      </button>
    </>
  )
}

export default CommunitySidebar
